import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { ApiClient, type ExpertType } from "../core/api-client.js";
import { LeadMagnetWidgetBase } from "../core/widget-base.js";
import { widgetStyles } from "../core/widget-styles.js";
import {
  simulationAmortissementFormSchema,
  simulationAmortissementStepSchemas,
} from "../lead-magnets/simulation-amortissement/form-schema.js";
import { mapSimulationAmortissementFormToSubmitPayload } from "../lead-magnets/simulation-amortissement/request-mapper.js";
import {
  getInitialSimulationAmortissementFormStep,
  getNextSimulationAmortissementFormStep,
  getPreviousSimulationAmortissementFormStep,
  getSimulationAmortissementFormProgress,
  type SimulationAmortissementFormModel,
  type SimulationAmortissementFormStep,
  type SimulationAmortissementPosteModel,
  type SimulationAmortissementResultModel,
  type SimulationAmortissementSlugParameter,
  type SimulationAmortissementWidgetViewState,
} from "../lead-magnets/simulation-amortissement/ui-state.js";

@customElement("lead-magnet-simulation-amortissement-widget")
export class LeadMagnetSimulationAmortissementWidget extends LeadMagnetWidgetBase {
  @property({ type: String })
  public override slug = "simulation-amortissement";

  @property({ type: String, attribute: "api-base-url" })
  public apiBaseUrl = "";

  @property({ type: String })
  public override source = "landing_simulation_amortissement_widget";

  @state()
  private viewState: SimulationAmortissementWidgetViewState = "idle";

  @state()
  private step: SimulationAmortissementFormStep =
    getInitialSimulationAmortissementFormStep();

  @state()
  private form: SimulationAmortissementFormModel = {
    email: "",
    firstName: "",
    source: "landing_simulation_amortissement_widget",
    consent: false,
    slugParameter: "france",
    prixAchat: "",
    travaux: "",
    ameublement: "",
    expertComptable: false,
    courtier: false,
    chasseurImmobilier: false,
    honeypot: "",
  };

  @state()
  private resultData: SimulationAmortissementResultModel | null = null;

  @state()
  private expertMessage = "";

  public static override styles = widgetStyles;

  protected override render() {
    const integrationError = this.getIntegrationError();
    if (integrationError) {
      return html`<p class="error">${integrationError}</p>`;
    }

    const progress = getSimulationAmortissementFormProgress(this.step);
    const isIntroStep = this.step === "intro";
    const isContactStep = this.step === "contact";
    const isResultStep = this.step === "result";
    const isExpertStep = this.step === "expert-optin";
    const hasPrevious = ["bien", "contact"].includes(this.step);

    return html`
      <form @submit=${this.onSubmit}>
        ${progress.current > 0
          ? html`<p class="step">
              Étape ${progress.current}/${progress.total}
            </p>`
          : null}
        ${this.renderCurrentStep()}

        <label style="display:none">
          Champ anti-bot
          <input
            name="honeypot"
            type="text"
            tabindex="-1"
            autocomplete="off"
            .value=${this.form.honeypot}
            @input=${(e: Event) => {
              this.form = {
                ...this.form,
                honeypot: (e.target as HTMLInputElement).value,
              };
            }}
          />
        </label>

        ${this.errorMessage
          ? html`<p class="error">${this.errorMessage}</p>`
          : null}
        ${isExpertStep
          ? null
          : html`
              <div class="actions">
                ${hasPrevious
                  ? html`
                      <button
                        type="button"
                        class="secondary"
                        ?disabled=${this.isLoading}
                        @click=${this.onPreviousStep}
                      >
                        Précédent
                      </button>
                    `
                  : null}
                ${isContactStep
                  ? html`
                      <button ?disabled=${this.isLoading}>
                        ${this.isLoading
                          ? "Envoi en cours..."
                          : "Recevoir mon estimation gratuite"}
                      </button>
                    `
                  : isResultStep
                    ? html`
                        <button
                          type="button"
                          class="secondary"
                          ?disabled=${this.isLoading}
                          @click=${this.resetFlow}
                        >
                          Recommencer
                        </button>
                      `
                    : html`
                        <button
                          type="button"
                          ?disabled=${this.isLoading}
                          @click=${this.onNextStep}
                        >
                          ${isIntroStep ? "Commencer" : "Suivant"}
                        </button>
                      `}
              </div>
            `}
      </form>
    `;
  }

  private renderCurrentStep() {
    switch (this.step) {
      case "intro":
        return this.renderIntroStep();
      case "bien":
        return this.renderBienStep();
      case "contact":
        return this.renderContactStep();
      case "result":
        return this.renderResultStep();
      case "expert-optin":
        return this.renderExpertStep();
      default:
        return html``;
    }
  }

  private renderIntroStep() {
    return html`
      <div class="intro-badge">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
        Gratuit - 2 min
      </div>

      <h2 class="intro-title">Estime l'amortissement LMNP de ton bien</h2>
      <p class="intro-copy">
        Renseigne ton prix d'achat et tes principaux frais pour voir combien
        d'amortissement tu peux générer chaque année.
      </p>

      <div class="intro-benefits">
        <div class="intro-benefit">
          <span class="check">✓</span>
          <span>Montant annuel et mensuel de ton amortissement</span>
        </div>
        <div class="intro-benefit">
          <span class="check">✓</span>
          <span>Détail par poste : bien, travaux et ameublement</span>
        </div>
        <div class="intro-benefit">
          <span class="check">✓</span>
          <span
            >Lecture simple pour préparer tes prochains arbitrages fiscaux</span
          >
        </div>
      </div>

      <div class="intro-footer">
        <span>🔒 Tes données restent confidentielles</span>
      </div>
    `;
  }

  private renderBienStep() {
    return html`
      <label>
        Où se situe ton bien ?
        <select
          name="slugParameter"
          .value=${this.form.slugParameter}
          @change=${(e: Event) => {
            this.form = {
              ...this.form,
              slugParameter: (e.target as HTMLSelectElement)
                .value as SimulationAmortissementSlugParameter,
            };
          }}
        >
          <option value="france">En France hors Paris</option>
          <option value="paris">À Paris</option>
        </select>
      </label>

      <label>
        Prix d'achat du bien
        <input
          name="prixAchat"
          type="text"
          inputmode="numeric"
          placeholder="200 000"
          .value=${this.formatThousands(this.form.prixAchat)}
          @input=${(e: Event) =>
            this.updateNumberField(
              "prixAchat",
              (e.target as HTMLInputElement).value,
            )}
        />
      </label>

      <label>
        Travaux
        <input
          name="travaux"
          type="text"
          inputmode="numeric"
          placeholder="30 000"
          .value=${this.formatThousands(this.form.travaux)}
          @input=${(e: Event) =>
            this.updateNumberField(
              "travaux",
              (e.target as HTMLInputElement).value,
            )}
        />
      </label>

      <label>
        Ameublement
        <input
          name="ameublement"
          type="text"
          inputmode="numeric"
          placeholder="5 000"
          .value=${this.formatThousands(this.form.ameublement)}
          @input=${(e: Event) =>
            this.updateNumberField(
              "ameublement",
              (e.target as HTMLInputElement).value,
            )}
        />
      </label>

      <p class="helper">
        Le paramétrage choisi (Paris ou France hors Paris) ajuste les hypothèses
        appliquees par le simulateur. Si tu ne renseignes pas les travaux ou
        l'ameublement, ce paramétrage sert de base au calcul.
      </p>
    `;
  }

  private renderContactStep() {
    const prixAchat = this.parseNumber(this.form.prixAchat);
    const travaux = this.parseNumber(this.form.travaux);
    const ameublement = this.parseNumber(this.form.ameublement);
    const totalBase = prixAchat + travaux + ameublement;

    return html`
      <div class="preview-card">
        <div class="preview-row">
          <span class="preview-label">Paramétrage choisi</span>
          <span class="preview-value"
            >${this.getProfileLabel(this.form.slugParameter)}</span
          >
        </div>
        <div class="preview-row">
          <span class="preview-label">Prix d'achat</span>
          <span class="preview-value">${this.formatCurrency(prixAchat)}</span>
        </div>
        <div class="preview-row">
          <span class="preview-label">Travaux</span>
          <span class="preview-value">${this.formatCurrency(travaux)}</span>
        </div>
        <div class="preview-row">
          <span class="preview-label">Ameublement</span>
          <span class="preview-value">${this.formatCurrency(ameublement)}</span>
        </div>
        <div class="preview-row">
          <span class="preview-label">Base suivie</span>
          <span class="preview-value">${this.formatCurrency(totalBase)}</span>
        </div>
        <div class="preview-row">
          <span class="preview-label">Amortissement annuel</span>
          <span class="preview-value blurred">X XXX EUR</span>
        </div>
        <div class="preview-row">
          <span class="preview-label">Amortissement mensuel</span>
          <span class="preview-value blurred">XXX EUR</span>
        </div>
      </div>

      <p class="unlock-title">Débloque ton estimation détaillée</p>
      <p class="helper">
        Le montant mensuel te donne un ordre de grandeur simple à rapprocher de
        ton loyer mensuel. Le montant annuel sert de repère pour raisonner sur
        une année fiscale complète.
      </p>

      <label>
        Email
        <input
          name="email"
          type="email"
          placeholder="ton@email.com"
          .value=${this.form.email}
          @input=${(e: Event) => {
            this.form = {
              ...this.form,
              email: (e.target as HTMLInputElement).value,
            };
          }}
          required
        />
      </label>

      <label>
        Prénom
        <input
          name="firstName"
          type="text"
          placeholder="Ton prénom"
          .value=${this.form.firstName}
          @input=${(e: Event) => {
            this.form = {
              ...this.form,
              firstName: (e.target as HTMLInputElement).value,
            };
          }}
        />
      </label>

      <label style="display: flex; align-items: center; gap: 8px;">
        <input
          name="consent"
          type="checkbox"
          .checked=${this.form.consent}
          @change=${(e: Event) => {
            this.form = {
              ...this.form,
              consent: (e.target as HTMLInputElement).checked,
            };
          }}
          required
        />
        <span>J'accepte de recevoir mon estimation par e-mail.</span>
      </label>

      <div class="reassurance">
        <span>🔒 Tes coordonnées sont confidentielles</span>
      </div>
    `;
  }

  private renderResultStep() {
    if (!this.resultData) {
      return html`
        <div class="result-card">
          <p class="result-title">Résultat indisponible</p>
          <p class="helper">
            Impossible d'afficher l'estimation pour le moment.
          </p>
        </div>
      `;
    }

    const totalPostesAnnuel = this.resultData.postes.reduce(
      (sum, poste) => sum + poste.montant_annuel,
      0,
    );
    const totalPostesMensuel = this.resultData.postes.reduce(
      (sum, poste) => sum + poste.montant_mensuel,
      0,
    );

    return html`
      <div class="result-card">
        <p class="result-title">Estimation de ton amortissement</p>

        <div class="preview-row">
          <span class="preview-label">Amortissement annuel</span>
          <span class="preview-value">
            ${this.formatCurrency(this.resultData.amortissement_annuel)}
          </span>
        </div>
        <div class="preview-row">
          <span class="preview-label">Amortissement mensuel</span>
          <span class="preview-value">
            ${this.formatCurrency(this.resultData.amortissement_mensuel)}
          </span>
        </div>
        <p class="helper">
          Le montant mensuel facilite la lecture de l'impact sur ton
          exploitation courante. L'annuel reste le bon niveau pour raisonner
          fiscalité.
        </p>

        ${this.resultData.postes.length > 0
          ? html`
              <table class="detail-table">
                <thead>
                  <tr>
                    <th>Poste</th>
                    <th>Base</th>
                    <th>Annuel</th>
                    <th>Mensuel</th>
                  </tr>
                </thead>
                <tbody>
                  ${this.resultData.postes.map(
                    (poste) => html`
                      <tr>
                        <td>${this.formatPosteLabel(poste.libelle)}</td>
                        <td>${this.formatCurrency(poste.montant_base)}</td>
                        <td>${this.formatCurrency(poste.montant_annuel)}</td>
                        <td>${this.formatCurrency(poste.montant_mensuel)}</td>
                      </tr>
                    `,
                  )}
                  <tr>
                    <td><strong>Total</strong></td>
                    <td></td>
                    <td>
                      <strong>${this.formatCurrency(totalPostesAnnuel)}</strong>
                    </td>
                    <td>
                      <strong
                        >${this.formatCurrency(totalPostesMensuel)}</strong
                      >
                    </td>
                  </tr>
                </tbody>
              </table>
            `
          : html`<p class="helper">Aucun poste détaillé n'a été retourné.</p>`}
        ${this.renderExpertSection()}
      </div>
    `;
  }

  private renderExpertStep() {
    return this.renderExpertSection();
  }

  private renderExpertSection() {
    return html`
      <div class="expert-card" style="margin-top: 0.75rem;">
        <span class="expert-eyebrow">Accompagnement</span>
        <p class="expert-title">
          Tu veux être mis en relation avec un expert de confiance ?
        </p>
        <p class="expert-copy">
          Sélectionne les partenaires que tu veux solliciter. Tu peux en choisir
          plusieurs selon ton besoin.
        </p>

        <div class="expert-options">
          <label class="expert-option">
            <input
              type="checkbox"
              .checked=${this.form.expertComptable}
              @change=${() => {
                this.form = {
                  ...this.form,
                  expertComptable: !this.form.expertComptable,
                };
              }}
            />
            <span>Expert-comptable</span>
          </label>
          <label class="expert-option">
            <input
              type="checkbox"
              .checked=${this.form.courtier}
              @change=${() => {
                this.form = { ...this.form, courtier: !this.form.courtier };
              }}
            />
            <span>Courtier</span>
          </label>
          <label class="expert-option">
            <input
              type="checkbox"
              .checked=${this.form.chasseurImmobilier}
              @change=${() => {
                this.form = {
                  ...this.form,
                  chasseurImmobilier: !this.form.chasseurImmobilier,
                };
              }}
            />
            <span>Chasseur immobilier</span>
          </label>
        </div>

        <button
          type="button"
          ?disabled=${this.isLoading}
          @click=${this.onSubmitExpertOptin}
        >
          ${this.isLoading
            ? "Envoi en cours..."
            : "Être mis en relation avec ces experts"}
        </button>

        ${this.expertMessage
          ? html`<p class="expert-feedback ${this.expertMessage.startsWith("Sélectionne") ? "error" : ""}">${this.expertMessage}</p>`
          : null}
      </div>
    `;
  }

  private onNextStep = (): void => {
    if (!this.validateStep(this.step)) {
      this.viewState = "error";
      return;
    }
    this.trackStep(this.step, {
      profile: this.form.slugParameter,
    });
    this.viewState = "idle";
    this.step = getNextSimulationAmortissementFormStep(this.step);
  };

  private onPreviousStep = (): void => {
    this.errorMessage = "";
    this.viewState = "idle";
    this.step = getPreviousSimulationAmortissementFormStep(this.step);
  };

  private onSubmit = async (event: SubmitEvent): Promise<void> => {
    event.preventDefault();
    if (this.step !== "contact") {
      this.onNextStep();
      return;
    }

    if (!this.validateStep("contact")) {
      this.viewState = "error";
      return;
    }

    const parsed = simulationAmortissementFormSchema.safeParse(
      this.buildFinalValidationInput(),
    );
    if (!parsed.success) {
      this.viewState = "error";
      this.setError(parsed.error.issues[0]?.message ?? "Formulaire invalide.");
      return;
    }

    this.startLoading();
    this.viewState = "submitting";

    try {
      const apiClient = new ApiClient(this.apiBaseUrl.trim());
      const payload = mapSimulationAmortissementFormToSubmitPayload({
        ...this.form,
        source: parsed.data.source,
        consent: parsed.data.consent,
      });

      const response = await apiClient.submitLeadMagnet(
        "simulation-amortissement",
        payload,
        this.makeIdempotencyKey(),
      );
      this.resultData = this.mapApiResult(response.result.payload);
      this.step = "result";
      this.viewState = "idle";
      this.errorMessage = "";
      this.trackSubmit({
        profile: this.form.slugParameter,
        prixAchat: this.parseNumber(this.form.prixAchat),
      });
      this.trackResult({
        profile: this.form.slugParameter,
        amortissementAnnuel: this.resultData.amortissement_annuel,
        amortissementMensuel: this.resultData.amortissement_mensuel,
        posteCount: this.resultData.postes.length,
      });
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : "Soumission impossible.";
      this.viewState = "error";
      this.setError(msg);
      this.trackError(this.step, msg, {
        profile: this.form.slugParameter,
      });
    } finally {
      this.stopLoading();
    }
  };

  private validateStep(step: SimulationAmortissementFormStep): boolean {
    if (step === "result" || step === "expert-optin") {
      this.errorMessage = "";
      return true;
    }

    const schema = simulationAmortissementStepSchemas[step];
    const parsed = schema.safeParse(this.buildStepValidationInput(step));
    if (!parsed.success) {
      this.setError(parsed.error.issues[0]?.message ?? "Formulaire invalide.");
      return false;
    }

    if (step === "contact") {
      this.form = { ...this.form, source: this.getSourceValue() };
    }

    this.errorMessage = "";
    return true;
  }

  private buildStepValidationInput(
    step: SimulationAmortissementFormStep,
  ): Record<string, unknown> {
    switch (step) {
      case "bien":
        return {
          slugParameter: this.form.slugParameter,
          prixAchat: this.form.prixAchat,
          travaux: this.form.travaux,
          ameublement: this.form.ameublement,
        };
      case "contact":
        return {
          email: this.form.email,
          firstName: this.form.firstName,
          source: this.getSourceValue(),
          consent: this.form.consent,
          honeypot: this.form.honeypot,
        };
      default:
        return {};
    }
  }

  private buildFinalValidationInput(): Record<string, unknown> {
    return { ...this.form, source: this.getSourceValue() };
  }

  private onSubmitExpertOptin = async (): Promise<void> => {
    const expertTypes = this.getExpertTypes();
    if (expertTypes.length === 0) {
      this.expertMessage = "Sélectionne au moins un expert pour être mis en relation.";
      return;
    }

    this.startLoading();
    this.errorMessage = "";
    try {
      const apiClient = new ApiClient(this.apiBaseUrl.trim());
      await apiClient.submitExpertOptin("simulation-amortissement", {
        email: this.form.email.trim(),
        firstName: this.form.firstName.trim() || undefined,
        source: this.getSourceValue(),
        expertTypes,
      });
      this.expertMessage =
        expertTypes.length > 0
          ? "Parfait, ta demande a bien été enregistrée."
          : "Merci, ton estimation est terminée.";
      this.trackCustomEvent("lm_expert_optin_submitted", {
        profile: this.form.slugParameter,
        expertTypes,
        expertCount: expertTypes.length,
      });
    } catch (error) {
      this.errorMessage =
        error instanceof Error ? error.message : "Soumission impossible.";
      this.trackError("result", this.errorMessage, {
        profile: this.form.slugParameter,
      });
    } finally {
      this.stopLoading();
    }
  };

  private resetFlow = (): void => {
    this.errorMessage = "";
    this.viewState = "idle";
    this.step = getInitialSimulationAmortissementFormStep();
    this.resultData = null;
    this.expertMessage = "";
    this.form = {
      email: "",
      firstName: "",
      source: this.getSourceValue(),
      consent: false,
      slugParameter: "france",
      prixAchat: "",
      travaux: "",
      ameublement: "",
      expertComptable: false,
      courtier: false,
      chasseurImmobilier: false,
      honeypot: "",
    };
  };

  private mapApiResult(
    payload: Record<string, unknown>,
  ): SimulationAmortissementResultModel {
    const postesRaw = Array.isArray(payload.postes) ? payload.postes : [];
    return {
      amortissement_annuel: toNumber(payload.amortissement_annuel) ?? 0,
      amortissement_mensuel: toNumber(payload.amortissement_mensuel) ?? 0,
      postes: postesRaw
        .map((poste) => mapPoste(poste))
        .filter(
          (poste): poste is SimulationAmortissementPosteModel => poste !== null,
        ),
    };
  }

  private getExpertTypes(): ExpertType[] {
    const expertTypes: ExpertType[] = [];
    if (this.form.expertComptable) expertTypes.push("expert_comptable");
    if (this.form.courtier) expertTypes.push("courtier");
    if (this.form.chasseurImmobilier) {
      expertTypes.push("chasseur_immobilier");
    }
    return expertTypes;
  }

  private makeIdempotencyKey(): string {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return `simamo_${crypto.randomUUID()}`;
    }
    return `simamo_${Date.now()}`;
  }

  private getSourceValue(): string {
    return this.source.trim() || this.form.source;
  }

  private updateNumberField(
    field: "prixAchat" | "travaux" | "ameublement",
    value: string,
  ): void {
    this.form = { ...this.form, [field]: value.replaceAll(/\D+/g, "") };
  }

  private parseNumber(value: string): number {
    const parsed = Number(value.replaceAll(/\s+/g, ""));
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  }

  private formatCurrency(value: number): string {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(value);
  }

  private formatThousands(value: string): string {
    const digits = value.replaceAll(/\D+/g, "").replace(/^0+(?=\d)/, "");
    if (!digits) return "";
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  }

  private formatPosteLabel(value: string): string {
    const normalizedValue =
      value.trim().toLocaleUpperCase("fr-FR") === "IGT"
        ? "Installation Générales et Techniques"
        : value;

    return normalizedValue
      .toLocaleLowerCase("fr-FR")
      .replace(
        /(^|[\s\-/'’(])(\p{L})/gu,
        (match, prefix: string, letter: string) =>
          `${prefix}${letter.toLocaleUpperCase("fr-FR")}`,
      );
  }

  private getProfileLabel(
    profile: SimulationAmortissementSlugParameter,
  ): string {
    return profile === "paris" ? "Paris" : "France hors Paris";
  }

  private getIntegrationError(): string | null {
    if (this.slug !== "simulation-amortissement") {
      return 'Ce widget supporte uniquement le slug "simulation-amortissement".';
    }
    const baseUrl = this.apiBaseUrl.trim();
    if (!baseUrl) {
      return "Configuration invalide: l'attribut `api-base-url` est obligatoire.";
    }
    try {
      const parsed = new URL(baseUrl);
      if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
        return "Configuration invalide: `api-base-url` doit utiliser http ou https.";
      }
    } catch {
      return "Configuration invalide: `api-base-url` doit être une URL absolue.";
    }
    return null;
  }
}

function mapPoste(raw: unknown): SimulationAmortissementPosteModel | null {
  if (typeof raw !== "object" || raw === null) return null;
  const value = raw as Record<string, unknown>;
  return {
    libelle: typeof value.libelle === "string" ? value.libelle : "",
    base_calcul: typeof value.base_calcul === "string" ? value.base_calcul : "",
    taux: toNumber(value.taux) ?? 0,
    duree: toNumber(value.duree) ?? 0,
    montant_base: toNumber(value.montant_base) ?? 0,
    montant_annuel: toNumber(value.montant_annuel) ?? 0,
    montant_mensuel: toNumber(value.montant_mensuel) ?? 0,
  };
}

function toNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value.replaceAll(/\s+/g, ""));
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

declare global {
  interface HTMLElementTagNameMap {
    "lead-magnet-simulation-amortissement-widget": LeadMagnetSimulationAmortissementWidget;
  }
}
